import React, { useEffect, useState } from 'react';
import { css } from '@emotion/core';
import { theme } from '@weave/theme-original';
import {
  DropdownField,
  EditIcon,
  NakedButton,
  PrimaryButton,
  SecondaryButton,
  Text,
  TextareaField,
  useFormField,
} from '@weave/design-system';
import { CriterionExceptionType } from '../../handoff-checklist-v2.types';

interface ExceptionCellProps {
  exception: string;
  metricId: string;
  criteriaIndex: number;
  isEditing: boolean;
  readOnly: boolean;
  allowException: boolean;
  exceptionOptions?: string[];
  exceptionType?: CriterionExceptionType;
  onStartEdit: () => void;
  onStopEdit: () => void;
  onExceptionChange?: (
    metricId: string,
    criteriaIndex: number,
    exception: string
  ) => void;
}

export const ExceptionCell = ({
  exception,
  metricId,
  criteriaIndex,
  isEditing,
  readOnly,
  allowException,
  exceptionOptions,
  exceptionType,
  onStartEdit,
  onStopEdit,
  onExceptionChange,
}: ExceptionCellProps) => {
  const exceptionFieldProps = useFormField({
    type: 'text',
    value: exception,
    required: true,
  });

  const dropdownFieldProps = useFormField(
    {
      type: 'dropdown',
      value: exception,
    },
    [exception]
  );

  const hasDropdownOptions =
    exceptionType === 'dropdown' &&
    Array.isArray(exceptionOptions) &&
    exceptionOptions.length > 0;

  useEffect(() => {
    if (hasDropdownOptions && dropdownFieldProps.value !== exception) {
      onExceptionChange?.(metricId, criteriaIndex, dropdownFieldProps.value);
    }
  }, [
    dropdownFieldProps.value,
    hasDropdownOptions,
    metricId,
    criteriaIndex,
    exception,
    onExceptionChange,
  ]);

  if (!allowException) {
    return (
      <Text size="medium" color="light" as="div">
        —
      </Text>
    );
  }

  if (readOnly) {
    return (
      <Text size="medium" as="div">
        {exception || (
          <span style={{ fontStyle: 'italic', color: '#8f95a3' }}>
            No exception provided.
          </span>
        )}
      </Text>
    );
  }

  if (exceptionType === 'dropdown') {
    if (!hasDropdownOptions) {
      return (
        <Text size="medium" color="light" as="div">
          —
        </Text>
      );
    }

    return (
      <div css={styles.dropdownContainer}>
        <DropdownField
          {...dropdownFieldProps}
          name={`exception-${criteriaIndex}`}
          label=""
          placeholder="Select exception"
        >
          {exceptionOptions?.map((option) => (
            <DropdownField.Option key={option} value={option}>
              {option}
            </DropdownField.Option>
          ))}
        </DropdownField>
      </div>
    );
  }

  if (exceptionType === 'free_text') {
    if (isEditing) {
      return (
        <div css={styles.editContainer}>
          <TextareaField
            {...exceptionFieldProps}
            name={`exception-${criteriaIndex}`}
            label="Exception Note"
            placeholder="Enter exception reason..."
            autoFocus
          />
          <div css={styles.saveButtonRow}>
            <SecondaryButton size="tiny" onClick={onStopEdit}>
              Cancel
            </SecondaryButton>
            <PrimaryButton
              size="tiny"
              disabled={!exceptionFieldProps.value.trim() && !exception}
              onClick={() => {
                onExceptionChange?.(
                  metricId,
                  criteriaIndex,
                  exceptionFieldProps.value.trim()
                );
                onStopEdit();
              }}
            >
              Save Note
            </PrimaryButton>
          </div>
        </div>
      );
    }

    if (!exception) {
      return (
        <div css={styles.exceptionCell}>
          <NakedButton onClick={onStartEdit} aria-label="Add Note" css={styles.button}>
            <EditIcon color="primary" size={16} />
            <Text size="medium" color="primary" as="span">
              Add Note
            </Text>
          </NakedButton>
        </div>
      );
    }

    return (
      <div css={styles.exceptionCell}>
        <Text size="medium" as="div">
          {exception}
        </Text>
        <NakedButton onClick={onStartEdit} aria-label="Edit Note" css={styles.button}>
          <EditIcon color="light" size={16} />
        </NakedButton>
      </div>
    );
  }

  return (
    <Text size="medium" color="light" as="div">
      —
    </Text>
  );
};

const styles = {
  editContainer: css`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `,
  saveButtonRow: css`
    display: flex;
    justify-content: flex-end;
    gap: ${theme.spacing(1)};
  `,
  exceptionCell: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${theme.spacing(2)};
  `,
  dropdownContainer: css`
    width: 100%;

    label {
      display: none;
    }
  `,
  button: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,
};
